import * as THREE from 'three';
import { MeshoptSimplifier } from 'meshoptimizer';
import { settings } from '@/config';
import { createLogger } from '@/utils/logger';
import type { Camera } from '@/camera/Camera';

/**
 * AUTO-LOD (2026-07-23, user: "auto LODs / progressive meshes like Needle — triangles are so
 * much, I want it very optimized on each scroll"). The same idea Needle ships, built natively:
 * every heavy mesh gets SIMPLIFIED index buffers generated at load by meshoptimizer (the exact
 * library Needle uses), and the renderer swaps between them by camera distance — full detail
 * for whatever the current scroll position is looking at, a fraction of the triangles for
 * everything far away. On this scene's layout (chapters spread along a long flight path) that
 * cuts the drawn triangle count massively at any given frame.
 *
 * DESIGN — geometry swap, NOT THREE.LOD nodes: wrapping meshes in LOD objects would
 * restructure the scene graph, breaking the baked per-node animation tracks and every
 * name-based system (material resolver, scene groups, colliders). Instead each mesh KEEPS its
 * node; we only reassign `mesh.geometry` between variants. The variants share the original
 * vertex attribute buffers (only the index differs), so the added GPU memory is just the
 * extra index buffers — near-free.
 *
 * Simplification runs once after model load (wasm, fast), with LockBorder so seams between
 * adjacent meshes can't crack open, and an error bound so silhouettes survive — a mesh that
 * can't simplify without visible damage simply keeps more triangles.
 */

interface LodEntry {
  mesh: THREE.Mesh;
  /** [full, mid, low] — mid/low may be null when simplification produced no real win. */
  levels: [THREE.BufferGeometry, THREE.BufferGeometry | null, THREE.BufferGeometry | null];
  center: THREE.Vector3;
  radius: number;
  current: number;
}

export class LodSystem {
  private readonly entries: LodEntry[] = [];
  private readonly log = createLogger('lod');
  private accumulator = 0;
  private built = false;

  constructor(private readonly camera: Camera) {}

  /** Generate the simplified variants for every eligible mesh under `root`. Async only for
   *  the wasm module's ready promise — the simplification itself is fast and synchronous. */
  async build(root: THREE.Object3D, exclude: ReadonlySet<THREE.Object3D>): Promise<void> {
    const cfg = settings.lod;
    await MeshoptSimplifier.ready;

    root.updateMatrixWorld(true);
    let trisFull = 0;
    let trisLow = 0;
    const scale = new THREE.Vector3();

    root.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh || exclude.has(mesh)) return;
      const geometry = mesh.geometry;
      const index = geometry.getIndex();
      const position = geometry.getAttribute('position');
      if (!index || !position || (position as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute) return;
      if (position.itemSize !== 3) return;
      const triCount = index.count / 3;
      if (triCount < cfg.minTris) return;

      const indices = index.array instanceof Uint32Array ? index.array : new Uint32Array(index.array);
      const positions = position.array as Float32Array;

      const mid = this.simplifyTo(indices, positions, cfg.midRatio, 0.01);
      const low = this.simplifyTo(indices, positions, cfg.lowRatio, 0.05);
      if (!mid && !low) return;

      geometry.computeBoundingSphere();
      const sphere = geometry.boundingSphere!;
      const center = mesh.localToWorld(sphere.center.clone());
      mesh.getWorldScale(scale);
      const radius = sphere.radius * Math.max(Math.abs(scale.x), Math.abs(scale.y), Math.abs(scale.z));

      this.entries.push({
        mesh,
        levels: [
          geometry,
          mid ? this.variantOf(geometry, mid) : null,
          low ? this.variantOf(geometry, low) : null,
        ],
        center,
        radius,
        current: 0,
      });
      trisFull += triCount;
      trisLow += (low ?? mid ?? indices).length / 3;
    });

    this.built = true;
    this.log.info(
      `auto-LOD ready: ${this.entries.length} meshes, ${Math.round(trisFull / 1000)}k tris full ` +
        `→ ${Math.round(trisLow / 1000)}k at lowest detail.`,
    );
  }

  /** meshopt simplify to a target ratio under an error bound; null when the win is trivial. */
  private simplifyTo(
    indices: Uint32Array,
    positions: Float32Array,
    ratio: number,
    error: number,
  ): Uint32Array | null {
    const target = Math.max(Math.floor((indices.length * ratio) / 3) * 3, 3);
    // LockBorder keeps shared edges between neighbouring meshes intact — no seam cracks.
    const [simplified] = MeshoptSimplifier.simplify(indices, positions, 3, target, error, [
      'LockBorder',
    ]);
    if (simplified.length >= indices.length * 0.9) return null; // no meaningful win
    return simplified;
  }

  /** A geometry that SHARES every vertex attribute with the source — only the index differs. */
  private variantOf(source: THREE.BufferGeometry, indices: Uint32Array): THREE.BufferGeometry {
    const geometry = new THREE.BufferGeometry();
    for (const name of Object.keys(source.attributes)) {
      geometry.setAttribute(name, source.getAttribute(name)!);
    }
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));
    geometry.boundingSphere = source.boundingSphere?.clone() ?? null;
    geometry.boundingBox = source.boundingBox?.clone() ?? null;
    return geometry;
  }

  /** Per-tick (throttled): pick each mesh's detail level from camera distance, with a dead
   *  zone so a camera hovering on a boundary never flickers between levels. */
  update(delta: number): void {
    const cfg = settings.lod;
    if (!this.built || !cfg.enabled || this.entries.length === 0) return;
    this.accumulator += delta;
    if (this.accumulator < cfg.checkIntervalSec) return;
    this.accumulator = 0;

    const camPos = this.camera.instance.position;
    const mid = cfg.midDistance * cfg.distanceScale;
    const low = cfg.lowDistance * cfg.distanceScale;

    for (const entry of this.entries) {
      // Distance to the mesh's SURFACE (bound), not its centre — big meshes stay detailed
      // while the camera is anywhere near them.
      const dist = Math.max(camPos.distanceTo(entry.center) - entry.radius, 0);
      let level = dist >= low ? 2 : dist >= mid ? 1 : 0;
      // Hysteresis: moving to MORE detail requires clearing the boundary by the dead zone.
      if (level < entry.current) {
        const boundary = entry.current === 2 ? low : mid;
        if (dist > boundary - cfg.hysteresis) level = entry.current;
      }
      // Fall back toward full detail when a variant is missing.
      while (level > 0 && entry.levels[level] === null) level -= 1;
      if (level !== entry.current) {
        entry.current = level;
        entry.mesh.geometry = entry.levels[level]!;
      }
    }
  }

  dispose(): void {
    for (const entry of this.entries) {
      entry.mesh.geometry = entry.levels[0];
      // Only the variant index buffers are ours to free — the attributes belong to the source.
      entry.levels[1]?.dispose();
      entry.levels[2]?.dispose();
    }
    this.entries.length = 0;
  }
}
